import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { ENV } from '../../config/env';

interface Company {
    id: string;
    email: string;
    companyName: string;
    website?: string;
    industry?: string;
    companySize?: string;
    status: 'pending' | 'approved' | 'rejected';
    createdAt: string;
    reviewedAt?: string;
}

interface ApiResponse {
    payload: {
        data: Company[];
        total: number;
        page: number;
        limit: number;
        totalPages: number;
    };
    status: {
        msg: string;
        error: boolean;
    };
}

/**
 * Review Companies Page
 * Admin page to review and approve/reject company registrations
 * Route: /admin/review-companies
 */
const ReviewCompanies: React.FC = () => {
    const navigate = useNavigate();
    const [companies, setCompanies] = useState<Company[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [filter, setFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('pending');
    const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);

    // Fetch companies
    useEffect(() => {
        fetchCompanies();
    }, [filter]);

    const fetchCompanies = async () => {
        setLoading(true);
        setError(null);

        try {
            const endpoint = filter === 'pending'
                ? `${ENV.COMPANY_SERVICE_URL}/review/v1/pending`
                : `${ENV.COMPANY_SERVICE_URL}/review/v1/list${filter !== 'all' ? `?status=${filter}` : ''}`;

            const response = await fetch(endpoint);
            const data: ApiResponse = await response.json();

            if (data.status.error) {
                throw new Error(data.status.msg || 'Failed to fetch companies');
            }

            setCompanies(data.payload.data || []);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred');
            console.error('Error fetching companies:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleViewDetails = (company: Company) => {
        setSelectedCompany(company);
        setShowDetailModal(true);
    };

    const handleApprove = async (companyId: string) => {
        setActionLoading(true);
        try {
            const response = await fetch(`${ENV.COMPANY_SERVICE_URL}/review/v1/${companyId}/approve`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            const data = await response.json();

            if (data.status.error) {
                throw new Error(data.status.msg || 'Failed to approve company');
            }

            await fetchCompanies();
            setShowDetailModal(false);
            setSelectedCompany(null);
        } catch (err) {
            alert(err instanceof Error ? err.message : 'Failed to approve company');
        } finally {
            setActionLoading(false);
        }
    };

    const handleReject = async (companyId: string) => {
        setActionLoading(true);
        try {
            const response = await fetch(`${ENV.COMPANY_SERVICE_URL}/review/v1/${companyId}/reject`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            const data = await response.json();

            if (data.status.error) {
                throw new Error(data.status.msg || 'Failed to reject company');
            }

            await fetchCompanies();
            setShowDetailModal(false);
            setSelectedCompany(null);
        } catch (err) {
            alert(err instanceof Error ? err.message : 'Failed to reject company');
        } finally {
            setActionLoading(false);
        }
    };

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'pending':
                return <span className="text-xs px-2 py-1 rounded bg-[#FFF4E6] text-[#FF9800]">Pending Review</span>;
            case 'approved':
                return <span className="text-xs px-2 py-1 rounded bg-[#E8F5E9] text-[#4CAF50]">Approved</span>;
            case 'rejected':
                return <span className="text-xs px-2 py-1 rounded bg-[#FFEEEE] text-[#FF5210]">Rejected</span>;
            default:
                return <span className="text-xs px-2 py-1 rounded bg-[#F1F3F4] text-[#757575]">{status}</span>;
        }
    };

    const formatDate = (dateString?: string) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    return (
        <AdminLayout
            showHeader={true}
            headerTitle="Review Companies"
            headerSubtitle="Review and approve company registrations"
        >
            <div>
                {/* Filter Tabs */}
                <div className="flex justify-end mb-6">
                    <div className="flex border border-[#CBCBCB] rounded-lg overflow-hidden">
                        <button
                            onClick={() => setFilter('pending')}
                            className={`px-3 md:px-4 py-1.5 text-xs md:text-sm font-medium ${
                                filter === 'pending' ? 'text-white bg-[#015FA6]' : 'text-[#757575]'
                            }`}
                        >
                            Pending
                        </button>
                        <button
                            onClick={() => setFilter('all')}
                            className={`px-3 md:px-4 py-1.5 text-xs md:text-sm font-medium ${
                                filter === 'all' ? 'text-white bg-[#015FA6]' : 'text-[#757575]'
                            }`}
                        >
                            All
                        </button>
                        <button
                            onClick={() => setFilter('approved')}
                            className={`px-3 md:px-4 py-1.5 text-xs md:text-sm font-medium ${
                                filter === 'approved' ? 'text-white bg-[#015FA6]' : 'text-[#757575]'
                            }`}
                        >
                            Approved
                        </button>
                        <button
                            onClick={() => setFilter('rejected')}
                            className={`px-3 md:px-4 py-1.5 text-xs md:text-sm font-medium ${
                                filter === 'rejected' ? 'text-white bg-[#015FA6]' : 'text-[#757575]'
                            }`}
                        >
                            Rejected
                        </button>
                    </div>
                </div>

                {/* Companies List */}
                <div className="bg-white rounded-2xl p-4 md:p-6">
                    {loading ? (
                        <div className="text-center py-12">
                            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#015FA6]"></div>
                            <p className="text-[#757575] mt-4">Loading companies...</p>
                        </div>
                    ) : error ? (
                        <div className="text-center py-12">
                            <p className="text-[#FF5210] text-base">{error}</p>
                            <button
                                onClick={fetchCompanies}
                                className="mt-4 px-6 py-2 bg-[#015FA6] text-white rounded-lg hover:bg-[#014a85]"
                            >
                                Retry
                            </button>
                        </div>
                    ) : companies.length === 0 ? (
                        <div className="text-center py-12">
                            <svg className="w-16 h-16 mx-auto mb-4 text-[#CBCBCB]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                            </svg>
                            <p className="text-[#757575] text-base">No companies found</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="border-b border-[#EDEDED]">
                                        <th className="text-left py-3 px-4 text-sm font-semibold text-[#0B111E]">Company Name</th>
                                        <th className="text-left py-3 px-4 text-sm font-semibold text-[#0B111E]">Email</th>
                                        <th className="text-left py-3 px-4 text-sm font-semibold text-[#0B111E]">Industry</th>
                                        <th className="text-left py-3 px-4 text-sm font-semibold text-[#0B111E]">Applied Date</th>
                                        <th className="text-left py-3 px-4 text-sm font-semibold text-[#0B111E]">Status</th>
                                        <th className="text-left py-3 px-4 text-sm font-semibold text-[#0B111E]">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {companies.map((company) => (
                                        <tr key={company.id} className="border-b border-[#F9F9F9] hover:bg-[#F9F9F9]">
                                            <td className="py-3 px-4 text-sm text-[#0B111E] font-medium">
                                                {company.companyName || 'N/A'}
                                            </td>
                                            <td className="py-3 px-4 text-sm text-[#515151]">{company.email}</td>
                                            <td className="py-3 px-4 text-sm text-[#515151]">{company.industry || 'N/A'}</td>
                                            <td className="py-3 px-4 text-sm text-[#515151]">{formatDate(company.createdAt)}</td>
                                            <td className="py-3 px-4">{getStatusBadge(company.status)}</td>
                                            <td className="py-3 px-4">
                                                <button
                                                    onClick={() => handleViewDetails(company)}
                                                    className="text-sm font-medium text-[#015FA6] hover:underline"
                                                >
                                                    View Details
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Detail Modal */}
            {showDetailModal && selectedCompany && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center p-4"
                    style={{ backdropFilter: 'blur(8px)', backgroundColor: 'rgba(0, 0, 0, 0.3)' }}
                    onClick={() => setShowDetailModal(false)}
                >
                    <div
                        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="p-8 overflow-y-auto max-h-[90vh]">
                            <div className="flex justify-between items-start mb-6">
                                <div>
                                    <h2 className="text-2xl font-semibold text-[#0B111E]">Company Details</h2>
                                    <p className="text-sm text-[#757575] mt-1">
                                        Registered {formatDate(selectedCompany.createdAt)}
                                    </p>
                                </div>
                                <button
                                    onClick={() => setShowDetailModal(false)}
                                    className="text-[#757575] hover:text-[#0B111E] transition-colors"
                                >
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>

                            <div className="space-y-5">
                                <div>
                                    <label className="text-sm font-medium text-[#757575] block mb-1">Company Name</label>
                                    <p className="text-base text-[#0B111E] font-medium">{selectedCompany.companyName || 'N/A'}</p>
                                </div>

                                <div>
                                    <label className="text-sm font-medium text-[#757575] block mb-1">Email</label>
                                    <p className="text-base text-[#0B111E]">{selectedCompany.email}</p>
                                </div>

                                <div>
                                    <label className="text-sm font-medium text-[#757575] block mb-1">Website</label>
                                    <p className="text-base text-[#0B111E]">
                                        {selectedCompany.website ? (
                                            <a
                                                href={selectedCompany.website}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-[#015FA6] hover:underline"
                                            >
                                                {selectedCompany.website}
                                            </a>
                                        ) : 'N/A'}
                                    </p>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="text-sm font-medium text-[#757575] block mb-1">Industry</label>
                                        <p className="text-base text-[#0B111E]">{selectedCompany.industry || 'N/A'}</p>
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium text-[#757575] block mb-1">Company Size</label>
                                        <p className="text-base text-[#0B111E]">{selectedCompany.companySize || 'N/A'}</p>
                                    </div>
                                </div>

                                <div>
                                    <label className="text-sm font-medium text-[#757575] block mb-1">Status</label>
                                    <div className="mt-1">{getStatusBadge(selectedCompany.status)}</div>
                                </div>
                            </div>

                            {/* Action Buttons */}
                            {selectedCompany.status === 'pending' && (
                                <div className="flex gap-3 mt-8 pt-6 border-t border-[#EDEDED]">
                                    <button
                                        onClick={() => handleApprove(selectedCompany.id)}
                                        disabled={actionLoading}
                                        className="flex-1 py-3 bg-[#4CAF50] text-white rounded-lg font-medium hover:bg-[#45a049] disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                                    >
                                        {actionLoading ? 'Processing...' : '✓ Approve'}
                                    </button>
                                    <button
                                        onClick={() => handleReject(selectedCompany.id)}
                                        disabled={actionLoading}
                                        className="flex-1 py-3 bg-[#FF5210] text-white rounded-lg font-medium hover:bg-[#e04a0e] disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                                    >
                                        {actionLoading ? 'Processing...' : '✗ Reject'}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </AdminLayout>
    );
};

export default ReviewCompanies;
